/**
 * Enumeraciones cerradas del sistema — §3.1 de la especificación.
 *
 * El código es estable y viaja por la API y la base de datos; la etiqueta es el
 * único texto que ve el usuario y vive siempre en español (§16.1).
 */

export const ESTADOS_OT = ['ABIERTA', 'EN_ESPERA', 'EN_CURSO', 'COMPLETA'] as const;
export type EstadoOT = (typeof ESTADOS_OT)[number];
export const ETIQUETA_ESTADO_OT: Record<EstadoOT, string> = {
  ABIERTA: 'Abierta',
  EN_ESPERA: 'En espera',
  EN_CURSO: 'En curso',
  COMPLETA: 'Completa',
};
/** Estados que cuentan como trabajo pendiente en contadores y filtros. */
export const ESTADOS_OT_ABIERTOS = ['ABIERTA', 'EN_ESPERA', 'EN_CURSO'] as const;

/**
 * Estado "visual" de una OT: el estado real, salvo que esté vencida (§14.1),
 * en cuyo caso ese hecho pesa más que el estado real para quien mira el
 * calendario o la lista. El backend decide `vencida` una sola vez (fecha de
 * vencimiento o, si no tiene, fecha de creación) y la UI solo la superpone.
 */
export const ESTADOS_VISUALES_OT = [...ESTADOS_OT, 'VENCIDA'] as const;
export type EstadoVisualOT = (typeof ESTADOS_VISUALES_OT)[number];
export const ETIQUETA_ESTADO_VISUAL_OT: Record<EstadoVisualOT, string> = {
  ...ETIQUETA_ESTADO_OT,
  VENCIDA: 'Vencida',
};

/** Superpone `vencida` sobre el estado real, salvo que ya esté completada. */
export function estadoVisualOt(estado: EstadoOT, vencida: boolean): EstadoVisualOT {
  return vencida && estado !== 'COMPLETA' ? 'VENCIDA' : estado;
}

export const PRIORIDADES = ['NINGUNA', 'BAJA', 'MEDIA', 'ALTA'] as const;
export type Prioridad = (typeof PRIORIDADES)[number];
export const ETIQUETA_PRIORIDAD: Record<Prioridad, string> = {
  NINGUNA: 'Ninguna',
  BAJA: 'Baja',
  MEDIA: 'Media',
  ALTA: 'Alta',
};
/** Orden de mayor a menor urgencia, para el ordenamiento por prioridad de §10.2. */
export const PESO_PRIORIDAD: Record<Prioridad, number> = { ALTA: 3, MEDIA: 2, BAJA: 1, NINGUNA: 0 };

export const TIPOS_TRABAJO = ['REACTIVA', 'PREVENTIVO', 'INSPECCION', 'OTRO'] as const;
export type TipoTrabajo = (typeof TIPOS_TRABAJO)[number];
export const ETIQUETA_TIPO_TRABAJO: Record<TipoTrabajo, string> = {
  REACTIVA: 'Reparación',
  PREVENTIVO: 'Mantenimiento preventivo',
  OTRO: 'Otro',
  INSPECCION: 'Inspección / revisión',
};

export const RECURRENCIAS = [
  'NO_SE_REPITE',
  'DIARIO',
  'SEMANAL',
  'MENSUAL_POR_FECHA',
  'MENSUAL_POR_DIA_SEMANA',
  'ANUAL',
] as const;
export type Recurrencia = (typeof RECURRENCIAS)[number];
export const ETIQUETA_RECURRENCIA: Record<Recurrencia, string> = {
  NO_SE_REPITE: 'No se repite',
  DIARIO: 'Diario',
  SEMANAL: 'Semanal',
  MENSUAL_POR_FECHA: 'Mensualmente por fecha',
  MENSUAL_POR_DIA_SEMANA: 'Mensualmente por día de la semana',
  ANUAL: 'Anual',
};


export const ESTADOS_SOLICITUD = ['PENDIENTE', 'APROBADA', 'DECLINADA'] as const;
export type EstadoSolicitud = (typeof ESTADOS_SOLICITUD)[number];
export const ETIQUETA_ESTADO_SOLICITUD: Record<EstadoSolicitud, string> = {
  PENDIENTE: 'Pendiente',
  APROBADA: 'Aprobada',
  DECLINADA: 'Declinada',
};

export const ESTADOS_ACTIVO = ['EN_LINEA', 'FUERA_DE_LINEA', 'NO_REALIZAR_SEGUIMIENTO'] as const;
export type EstadoActivo = (typeof ESTADOS_ACTIVO)[number];
export const ETIQUETA_ESTADO_ACTIVO: Record<EstadoActivo, string> = {
  EN_LINEA: 'En línea',
  FUERA_DE_LINEA: 'Fuera de línea',
  NO_REALIZAR_SEGUIMIENTO: 'No realizar seguimiento',
};

export const CRITICIDADES = ['CRITICO', 'IMPORTANTE', 'NORMAL'] as const;
export type Criticidad = (typeof CRITICIDADES)[number];
export const ETIQUETA_CRITICIDAD: Record<Criticidad, string> = {
  CRITICO: 'Crítico',
  IMPORTANTE: 'Importante',
  NORMAL: 'Normal',
};

export const TIPOS_ACCESO = ['ADMINISTRADOR', 'FULL_USER', 'SOLICITANTE'] as const;
export type TipoAcceso = (typeof TIPOS_ACCESO)[number];
export const ETIQUETA_TIPO_ACCESO: Record<TipoAcceso, string> = {
  ADMINISTRADOR: 'Administrador',
  FULL_USER: 'Full User',
  SOLICITANTE: 'Únicamente solicitante',
};

export const ESTADOS_INVITACION = ['INVITADO', 'ACTIVO', 'NUNCA_SE_UNIO'] as const;
export type EstadoInvitacion = (typeof ESTADOS_INVITACION)[number];
export const ETIQUETA_ESTADO_INVITACION: Record<EstadoInvitacion, string> = {
  INVITADO: 'Invitado',
  ACTIVO: 'Activo',
  NUNCA_SE_UNIO: 'Nunca se unió',
};

export const TIPOS_GRUPO = ['FUNCIONAL', 'BANDEJA_ENTRADA'] as const;
export type TipoGrupo = (typeof TIPOS_GRUPO)[number];
export const ETIQUETA_TIPO_GRUPO: Record<TipoGrupo, string> = {
  FUNCIONAL: 'Funcional',
  BANDEJA_ENTRADA: 'Bandeja de entrada',
};

export const TIPOS_MEDIDOR = ['MANUAL', 'AUTOMATIZADO'] as const;
export type TipoMedidor = (typeof TIPOS_MEDIDOR)[number];
export const ETIQUETA_TIPO_MEDIDOR: Record<TipoMedidor, string> = {
  MANUAL: 'Manual',
  AUTOMATIZADO: 'Automatizado',
};

export const UNIDADES_FRECUENCIA = ['NINGUNO', 'HORAS', 'DIAS', 'SEMANAS', 'MESES', 'ANIOS'] as const;
export type UnidadFrecuencia = (typeof UNIDADES_FRECUENCIA)[number];
export const ETIQUETA_UNIDAD_FRECUENCIA: Record<UnidadFrecuencia, string> = {
  NINGUNO: 'Ninguno',
  HORAS: 'Horas',
  DIAS: 'Días',
  SEMANAS: 'Semanas',
  MESES: 'Meses',
  ANIOS: 'Años',
};

export const RESULTADOS_INSPECCION = ['APROBADO', 'ALERTA', 'FALLA'] as const;
export type ResultadoInspeccion = (typeof RESULTADOS_INSPECCION)[number];

export const RESPUESTAS_TERNARIAS = ['SI', 'NO', 'NA'] as const;
export type RespuestaTernaria = (typeof RESPUESTAS_TERNARIAS)[number];
export const ETIQUETA_RESPUESTA_TERNARIA: Record<RespuestaTernaria, string> = {
  SI: 'Sí',
  NO: 'No',
  NA: 'N/A',
};

export const TIPOS_CAMPO_PROCEDIMIENTO = [
  'CASILLA',
  'TEXTO',
  'NUMERICO',
  'IMPORTE',
  'ELECCION_MULTIPLE',
  'LISTA_COMPROBACION',
  'INSPECCION',
  'SI_NO_NA',
  'IMAGEN_ARCHIVO',
  'FIRMA',
  'FECHA',
  'LECTURA_MEDIDOR',
] as const;
export type TipoCampoProcedimiento = (typeof TIPOS_CAMPO_PROCEDIMIENTO)[number];
export const ETIQUETA_TIPO_CAMPO_PROCEDIMIENTO: Record<TipoCampoProcedimiento, string> = {
  CASILLA: 'Casilla',
  TEXTO: 'Campo de texto',
  NUMERICO: 'Campo numérico',
  IMPORTE: 'Importe (S/)',
  ELECCION_MULTIPLE: 'Elección múltiple',
  LISTA_COMPROBACION: 'Lista de comprobación',
  INSPECCION: 'Comprobación de la inspección',
  SI_NO_NA: 'Sí, No, N/A',
  IMAGEN_ARCHIVO: 'Campo de imagen/archivo',
  FIRMA: 'Bloque de firma',
  FECHA: 'Fecha',
  LECTURA_MEDIDOR: 'Medidor Lectura',
};

export const TIPOS_ITEM_PLANTILLA = ['SECCION', 'ENCABEZADO', 'CAMPO', 'PROCEDIMIENTO_ANIDADO'] as const;
export type TipoItemPlantilla = (typeof TIPOS_ITEM_PLANTILLA)[number];
export const ETIQUETA_TIPO_ITEM_PLANTILLA: Record<TipoItemPlantilla, string> = {
  SECCION: 'Sección',
  ENCABEZADO: 'Encabezado',
  CAMPO: 'Campo',
  PROCEDIMIENTO_ANIDADO: 'Procedimiento anidado',
};

export const ESTADOS_PLANTILLA = ['BORRADOR', 'PUBLICADA', 'ARCHIVADA'] as const;
export type EstadoPlantilla = (typeof ESTADOS_PLANTILLA)[number];
export const ETIQUETA_ESTADO_PLANTILLA: Record<EstadoPlantilla, string> = {
  BORRADOR: 'Borrador',
  PUBLICADA: 'Publicada',
  ARCHIVADA: 'Archivada',
};

export const ESTADOS_EJECUCION = ['EN_PROGRESO', 'COMPLETADA'] as const;
export type EstadoEjecucion = (typeof ESTADOS_EJECUCION)[number];

export const TIPOS_TRANSACCION_PARTE = ['ENTRADA', 'SALIDA', 'AJUSTE', 'TRANSFERENCIA'] as const;
export type TipoTransaccionParte = (typeof TIPOS_TRANSACCION_PARTE)[number];
export const ETIQUETA_TIPO_TRANSACCION_PARTE: Record<TipoTransaccionParte, string> = {
  ENTRADA: 'Entrada',
  SALIDA: 'Salida',
  AJUSTE: 'Ajuste',
  TRANSFERENCIA: 'Transferencia',
};

export const ORIGENES_LECTURA = ['MANUAL', 'PROCEDIMIENTO', 'AUTOMATIZADO'] as const;
export type OrigenLectura = (typeof ORIGENES_LECTURA)[number];

export const TIPOS_CAMPO_PERSONALIZADO = [
  'TEXTO',
  'TEXTO_LARGO',
  'ENTERO',
  'DECIMAL',
  'MONEDA',
  'FECHA',
  'FECHA_HORA',
  'BOOLEANO',
  'SELECCION',
  'SELECCION_MULTIPLE',
] as const;
export type TipoCampoPersonalizado = (typeof TIPOS_CAMPO_PERSONALIZADO)[number];
export const ETIQUETA_TIPO_CAMPO_PERSONALIZADO: Record<TipoCampoPersonalizado, string> = {
  TEXTO: 'Texto',
  TEXTO_LARGO: 'Texto largo',
  ENTERO: 'Número entero',
  DECIMAL: 'Número decimal',
  MONEDA: 'Importe',
  FECHA: 'Fecha',
  FECHA_HORA: 'Fecha y hora',
  BOOLEANO: 'Sí / No',
  SELECCION: 'Selección',
  SELECCION_MULTIPLE: 'Selección múltiple',
};

/** Entidades que admiten campos personalizados (§16.7) y adjuntos (§16.3). */
export const ENTIDADES_EXTENSIBLES = [
  'UBICACION',
  'ACTIVO',
  'PARTE',
  'MEDIDOR',
  'ORDEN_TRABAJO',
  'SOLICITUD',
] as const;
export type EntidadExtensible = (typeof ENTIDADES_EXTENSIBLES)[number];

/** Entidades que pueden llevar adjuntos (`app.adjuntos.entidad`, §16.3). */
export const ENTIDADES_ADJUNTO = [
  'UBICACION',
  'ACTIVO',
  'PARTE',
  'MEDIDOR',
  'ORDEN_TRABAJO',
  'SOLICITUD',
  'COMENTARIO',
  'RESPUESTA_CAMPO',
  'REGISTRO_COSTO',
  'USUARIO',
  'OBSERVACION_ACTIVO',
  'ITEM_PLANTILLA',
  'OBSERVACION_UBICACION',
  'OBSERVACION_SUJETO',
] as const;
export type EntidadAdjunto = (typeof ENTIDADES_ADJUNTO)[number];

/** Entidades que comparten el espacio de nombres del código QR (§16.2). */
export const ENTIDADES_QR = ['UBICACION', 'ACTIVO', 'PARTE'] as const;
export type EntidadQR = (typeof ENTIDADES_QR)[number];

/**
 * Catálogo de eventos auditados (§10.5). La bitácora es append-only y alimenta
 * tanto el hilo unificado de la OT como la Actividad Reciente (§14.5).
 */
export const TIPOS_EVENTO_AUDITORIA = [
  'CREACION',
  'ACTUALIZACION',
  'ELIMINACION',
  'COMENTARIO',
  'CAMBIO_ESTADO',
  'PROGRESO_INICIADO',
  'PUESTA_EN_ESPERA',
  'COMPLETADA',
  'COMPLETADA_CON_ALERTAS',
  'REAPERTURA',
  'CAMBIO_PRIORIDAD',
  'ASIGNACION_UBICACION',
  'ASIGNACION_USUARIO',
  'PROCEDIMIENTO_ADJUNTADO',
  'PROCEDIMIENTO_REMOVIDO',
  'ACCION_CORRECTIVA_GENERADA',
  'ACCION_CORRECTIVA_ELIMINADA',
  'OT_REASIGNADA',
  'SOLICITUD_APROBADA',
  'SOLICITUD_DECLINADA',
  'SOLICITUD_RESTABLECIDA',
  'PARTE_CONSUMIDA',
  'TRANSACCION_PARTE',
  'AJUSTE_INVENTARIO',
  'CAMBIO_ESTADO_ACTIVO',
  'CAMBIO_UBICACION_ACTIVO',
  'LECTURA_REGISTRADA',
  'OCULTAMIENTO',
  'REVELACION',
] as const;
export type TipoEventoAuditoria = (typeof TIPOS_EVENTO_AUDITORIA)[number];

/** Catálogos administrables (§3.2). Un solo almacén, discriminado por código. */
export const CATALOGOS = [
  'FABRICANTE',
  'MODELO',
  'MOTIVO_INACTIVIDAD',
  'CATEGORIA_COSTO',
  'CATEGORIA_ENCARGADO',
  'TIPO_PARTE',
  'UNIDAD_MEDIDA_MEDIDOR',
  'FAMILIA_FALLA',
  'CATEGORIA_PROCEDIMIENTO',
  'ROL_SUJETO_EVALUABLE',
  'TIPO_FECHA_ACTIVO',
] as const;
export type CodigoCatalogo = (typeof CATALOGOS)[number];
export const ETIQUETA_CATALOGO: Record<CodigoCatalogo, string> = {
  FABRICANTE: 'Fabricantes',
  MODELO: 'Modelos',
  MOTIVO_INACTIVIDAD: 'Motivos de inactividad',
  CATEGORIA_COSTO: 'Categorías de costo',
  CATEGORIA_ENCARGADO: 'Categorías de encargado',
  TIPO_PARTE: 'Tipos de parte',
  UNIDAD_MEDIDA_MEDIDOR: 'Unidades de medida',
  FAMILIA_FALLA: 'Familias de falla',
  CATEGORIA_PROCEDIMIENTO: 'Categorías de procedimiento',
  ROL_SUJETO_EVALUABLE: 'Roles de sujeto evaluable',
  TIPO_FECHA_ACTIVO: 'Tipos de fecha de activo',
};
/** MODELO depende de FABRICANTE: catálogo jerárquico (§3.2, §5.3). */
export const CATALOGO_PADRE: Partial<Record<CodigoCatalogo, CodigoCatalogo>> = { MODELO: 'FABRICANTE' };

export const ESTADOS_SUSCRIPCION = ['PRUEBA', 'ACTIVA', 'SUSPENDIDA', 'CANCELADA'] as const;
export type EstadoSuscripcion = (typeof ESTADOS_SUSCRIPCION)[number];
export const ETIQUETA_ESTADO_SUSCRIPCION: Record<EstadoSuscripcion, string> = {
  PRUEBA: 'En prueba',
  ACTIVA: 'Activa',
  SUSPENDIDA: 'Suspendida',
  CANCELADA: 'Cancelada',
};
